import React, { useState } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiContentCopy, mdiCheck, mdiClose, mdiLoading } from '@mdi/js';
import { apiService } from '../services/apiService';
import { CircuitSchema } from '../types';
import { useEscapeKey } from '../hooks/useEscapeKey';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
`;

const Dialog = styled.div`
  background: #ffffff;
  border-radius: 8px;
  width: 420px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #e8eaed;
  font-weight: 600;
  font-size: 14px;
  color: #202124;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #5f6368;
  padding: 4px;
  border-radius: 4px;
  &:hover { background: #f1f3f4; }
`;

const Body = styled.div`
  padding: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #5f6368;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-size: 13px;
  margin-bottom: 14px;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #1a73e8; }
`;

const LinkRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const LinkInput = styled.input`
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-size: 12px;
  background: #f8f9fa;
  color: #202124;
  box-sizing: border-box;
`;

const CopyBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  border: none;
  border-radius: 4px;
  background: #202D61;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #313a6b; }
`;

const ShareBtn = styled.button`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 4px;
  background: #202D61;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  &:hover { background: #313a6b; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SpinIcon = styled(Icon)`
  animation: spin 1s linear infinite;
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

interface ShareDialogProps {
  schema: CircuitSchema;
  onClose: () => void;
}

export const ShareDialog: React.FC<ShareDialogProps> = ({ schema, onClose }) => {
  useEscapeKey(onClose);

  const [title, setTitle] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleShare = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiService.shareCircuit({
        title: title || 'Untitled Circuit',
        circuit_schema: schema,
      });
      setShareUrl(result.share_url);
      apiService.trackEvent('circuit_shared');
    } catch (err: any) {
      setError(err.message || 'Failed to create share link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.querySelector<HTMLInputElement>('#share-url-input');
      if (input) {
        input.select();
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Dialog onClick={e => e.stopPropagation()}>
        <Header>
          Share Circuit
          <CloseBtn onClick={onClose}>
            <Icon path={mdiClose} size={0.7} />
          </CloseBtn>
        </Header>
        <Body>
          {!shareUrl ? (
            <>
              <Label>Circuit title (optional)</Label>
              <Input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="My cooling circuit"
              />
              {error && <div style={{ color: '#c5221f', fontSize: 12, marginBottom: 10 }}>{error}</div>}
              <ShareBtn onClick={handleShare} disabled={loading}>
                {loading && <SpinIcon path={mdiLoading} size={0.6} />}
                Generate Share Link
              </ShareBtn>
            </>
          ) : (
            <>
              <Label>Share this link with others</Label>
              <LinkRow>
                <LinkInput
                  id="share-url-input"
                  value={shareUrl}
                  readOnly
                  onClick={e => (e.target as HTMLInputElement).select()}
                />
                <CopyBtn onClick={handleCopy}>
                  <Icon path={copied ? mdiCheck : mdiContentCopy} size={0.6} />
                  {copied ? 'Copied' : 'Copy'}
                </CopyBtn>
              </LinkRow>
            </>
          )}
        </Body>
      </Dialog>
    </Overlay>
  );
};
